// Context
import { useForm } from "../context/form-context/hooks";

// Interfaces
import { PaymentChoice, PaymentInformation } from "../interfaces/Payment";
import { Question, RadioQuestion } from "../interfaces/Question";
import { Summary, SummaryItem } from "../interfaces/Summary";

// i18n
import { useTranslation } from "../translations/useTranslation";

// Views
import { UNIONS } from "../views/Details";

const useSummaryAnswers = (): Summary[] => {
    // Hooks
    const { state } = useForm();
    const { t, m, keys } = useTranslation()

    const summaryInformation: Summary[] = [];

    const getComplementaryAnswers = (question: RadioQuestion): string[] => {
        const selectedAnswer = question.possibleAnswers.find(a => a.value === question.givenAnswer);
        if (selectedAnswer && selectedAnswer.complementaryFields && selectedAnswer.complementaryFields.length > 0) {
            return selectedAnswer.complementaryFields.map(f => {
                return `${t(f.label)}: ${question.complementaryAnswers ? question.complementaryAnswers[f.name] : ''}`
            });
        }
        return [];
    }

    const getSummarySection = (questions: Question[], title: string): Summary => {
        let workSummaryItems: SummaryItem[] = [];
        for (const question of questions) {
            if (question.type === 'radio') {
                const answer = question.possibleAnswers.find(a => a.value === question.givenAnswer);
                workSummaryItems.push({
                    question: t(question.question),
                    answer: answer ? t(answer.title) : question.givenAnswer || '',
                    complementaryAnswers: getComplementaryAnswers(question) //getWorkRadioAnswer(question)
                })
            } else if (question.type === 'text-input') {
                workSummaryItems.push({
                    question: t(question.question),
                    answer: question.givenAnswer || ''
                })
            }
        }
        return {
            title: title,
            items: workSummaryItems
        };
    }

    const getBankInfoSummary = (bankInfo: PaymentInformation | undefined) => {
        const infoSummary: string[] = [];
        const accountNumber = bankInfo?.clearingNumber ? `${bankInfo.clearingNumber}-${bankInfo.bankNumber}` : bankInfo?.bankNumber;
        if (bankInfo?.bank) {
            infoSummary.push(`Bank: ${bankInfo.bank}`)
        }
        infoSummary.push(`${t(keys.summaryAccount)}: ${accountNumber}`)
        return infoSummary;
    }

    const getPaymentSummary = (paymentChoice: PaymentChoice): Summary => {
        const items: SummaryItem[] = [];
        switch (paymentChoice.choice) {
            case 'autogiro':
                const bankInfo = paymentChoice.bankInfo;
                items.push({
                    question: t(keys.headingHowToPay),
                    answer: t(keys.paymentAutogiro),
                    complementaryAnswers: getBankInfoSummary(bankInfo)
                });
                break;
            case 'invoice':
                items.push({
                    question: t(keys.headingHowToPay),
                    answer: t(keys.paymentInvoice)
                })
                break;
            case 'association':
                const unionMatch = UNIONS.find(u => u.value === paymentChoice.viaAssociation);
                items.push({
                    question: t(keys.headingHowToPay),
                    answer: unionMatch ? `${t(keys.paymentViaAssociation)}: ${unionMatch.label}` : ''
                });
                break;
        }
        return {
            title: t(keys.summaryPayment),
            items: items
        }
    }

    const getContactDetailsLabel = (key: string) => {
        switch (key) {
            case 'personalIdentityNumber':
                return t(keys.formPersonalIdentityNumber);
            case 'firstName':
                return t(keys.formFirstName);
            case 'familyName':
                return t(keys.formFamilyName);
            case 'street':
                return t(keys.formStreetName);
            case 'city':
                return t(keys.formCity);
            case 'email':
                return t(keys.formEmailAddress);
            case 'postalCode':
                return t(keys.formPostalCode);
            case 'country':
                return t(keys.formCountry);
            case 'phoneNumber':
                return t(keys.formPhoneNumber);
            case 'memberCode':
                return t(keys.formMemberCode);
            default:
                return key;
        }
    }

    // Consent
    if (state.givenConsent !== undefined) {
        summaryInformation.push({
            title: t(keys.summaryPOA),
            items: [{
                question: m(keys.summaryPOAQuestion, '{name}', state.union || ''),
                answer: state.givenConsent ? t(keys.answerYes) : t(keys.answerNo)
            }]
        })
    }

    // Work
    const workQuestions = state.answersToQuestions.filter(a => a.category === 'work' && a.status === 'ANSWERED');
    if (workQuestions.length > 0) {
        summaryInformation.push(getSummarySection(workQuestions, t(keys.summaryWork)))
    }

    // Study
    const studyQuestions = state.answersToQuestions.filter(a => a.category === 'education' && a.status === 'ANSWERED');
    if (studyQuestions.length > 0) {
        summaryInformation.push(getSummarySection(studyQuestions, t(keys.summaryEducation)))
    }

    // Contact details
    const arr: SummaryItem[] = [];
    const contactEntries = Object.entries(state.contactInfo);

    // Sort the entries so that the personal identity number is always first
    contactEntries.sort(([key1], [key2]) => {
        if (key1 === 'personalIdentityNumber') {
            return -1;
        } else if (key2 === 'personalIdentityNumber') {
            return 1;
        } else {
            return 0;
        }
    });

    for (const [key, value] of contactEntries) {

        if (key === 'memberCode') {
            continue;
        }

        if (state.formMode === 'eId' && key === 'personalIdentityNumber') {
            continue;
        }

        if (key === 'emailRepeated') {
            continue;
        }

        arr.push({
            question: getContactDetailsLabel(key),
            answer: value
        });
    }
    summaryInformation.push({
        title: t(keys.summaryDetails),
        items: arr
    })

    // Payment information
    if (state.paymentInformation) {
        summaryInformation.push(getPaymentSummary(state.paymentInformation))
    }

    if (state.enableMemberCodeField && state.contactInfo.memberCode) {
        summaryInformation.push({
            title: t(keys.headingMemberCode),
            items: [{
                question: t(keys.formMemberCode),
                answer: state.contactInfo.memberCode
            }]
        })
    }

    return summaryInformation;
};

export default useSummaryAnswers;